/**
 * Text Editor Utility Functions
 *
 * Provides helper functions for working with Quill editor content, including delta to HTML conversion for rendering in
 * other components.
 */

import type { QuillDelta } from './quill.types';

/**
 * Converts Quill Delta JSON to HTML string.
 *
 * This is useful when you store content in delta format but need to render it in components that expect HTML (like
 * HxNote).
 *
 * @example
 *   ```typescript
 *   const deltaJson = '{"ops":[{"insert":"Hello "},{"attributes":{"bold":true},"insert":"World"}]}';
 *   const html = await convertDeltaToHtml(deltaJson);
 *   // Returns: '<p>Hello <strong>World</strong></p>'
 *   ```;
 *
 * @param deltaJson - Delta JSON string or object
 * @returns HTML string representation of the delta content
 */
export async function convertDeltaToHtml(deltaJson: string | QuillDelta | any): Promise<string> {
  try {
    // Dynamic import of Quill (SSR-safe)
    const QuillModule = await import('quill');
    const Quill = QuillModule.default;

    // Create temporary container
    const tempContainer = document.createElement('div');
    tempContainer.style.display = 'none';
    document.body.appendChild(tempContainer);

    // Create Quill instance
    const quill = new Quill(tempContainer, {
      theme: 'snow',
      modules: {
        toolbar: false,
      },
    });

    // Parse delta if it's a string
    const delta = typeof deltaJson === 'string' ? JSON.parse(deltaJson) : deltaJson;

    // Set content
    quill.setContents(delta as any);

    // Get HTML
    const html = quill.root.innerHTML;

    // Cleanup
    document.body.removeChild(tempContainer);

    return html;
  } catch (error) {
    console.error('Error converting delta to HTML:', error);
    return '';
  }
}

/**
 * Converts HTML string to Quill Delta JSON.
 *
 * This is useful when you have HTML content and need to convert it to delta format for the text editor.
 *
 * @example
 *   ```typescript
 *   const html = '<p>Hello <strong>World</strong></p>';
 *   const delta = await convertHtmlToDelta(html);
 *   // Returns: {"ops":[{"insert":"Hello "},{"attributes":{"bold":true},"insert":"World\n"}]}
 *   ```;
 *
 * @param html - HTML string
 * @returns Delta JSON object
 */
export async function convertHtmlToDelta(html: string): Promise<any> {
  try {
    // Dynamic import of Quill (SSR-safe)
    const QuillModule = await import('quill');
    const Quill = QuillModule.default;

    // Create temporary container
    const tempContainer = document.createElement('div');
    tempContainer.style.display = 'none';
    document.body.appendChild(tempContainer);

    // Create Quill instance
    const quill = new Quill(tempContainer, {
      theme: 'snow',
      modules: {
        toolbar: false,
      },
    });

    // Set HTML content
    quill.clipboard.dangerouslyPasteHTML(html);

    // Get delta
    const delta = quill.getContents();

    // Cleanup
    document.body.removeChild(tempContainer);

    return delta;
  } catch (error) {
    console.error('Error converting HTML to delta:', error);
    return { ops: [] };
  }
}

/**
 * Checks if a string is valid delta JSON format.
 *
 * @param value - String to check
 * @returns True if valid delta JSON
 */
export function isValidDeltaJson(value: string): boolean {
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && Array.isArray(parsed.ops);
  } catch {
    return false;
  }
}

/**
 * Gets plain text from delta JSON.
 *
 * @example
 *   ```typescript
 *   const deltaJson = '{"ops":[{"insert":"Hello "},{"attributes":{"bold":true},"insert":"World"}]}';
 *   const text = getPlainTextFromDelta(deltaJson);
 *   // Returns: 'Hello World'
 *   ```;
 *
 * @param deltaJson - Delta JSON string or object
 * @returns Plain text content without formatting
 */
export function getPlainTextFromDelta(deltaJson: string | QuillDelta): string {
  try {
    const delta: QuillDelta = typeof deltaJson === 'string' ? JSON.parse(deltaJson) : deltaJson;

    if (!delta.ops) {
      return '';
    }

    return delta.ops
      .map((op) => {
        if (typeof op.insert === 'string') {
          return op.insert;
        }
        return '';
      })
      .join('');
  } catch (error) {
    console.error('Error extracting plain text from delta:', error);
    return '';
  }
}
